import {
  cartSlice,
  findCartVehicle,
  getUpdatedPartsList,
  SellerCartVehicle,
  SellerWithLocations,
  setQtyAtLocationBulk,
  showCartPartComparisionNotification,
} from 'cart'
import { endOfDay, format } from 'date-fns'
import { ProductLocationModel, LineType } from 'interfaces'
import { makeAutoObservable, toJS } from 'mobx'
import { createContext, useContext } from 'react'
import MultiSellerServicesProvider, {
  ISeller,
} from 'src/services/MultiSellerServicesProvider'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { LaborItem } from 'src/store/models/LaborModel'
import {
  CartVehicle,
  OrderFormData,
  ShoppingCartProduct,
} from 'src/store/models/ShoppingCartModels'
import { VehicleSpecificationCartItem } from 'src/store/models/VehicleSpecification'
import { Vehicle } from 'src/store/models/Vehicles'
import {
  Part,
  QuoteLabor,
  QuoteResponse,
  QuotesFilters,
  QuotesHistoryItem,
  QuoteSpecification,
} from '../../../../src/api/quote/interface'
import miscellaneousVehicle from '../../../../src/features/search/Results/utils/miscellaneousVehicle'
import history from '../../../../src/helpers/history'
import { LocationType } from '../../../../src/helpers/locationType'
import ShowNotification from '../../../../src/ui-components/modals/ToastNotifcationNotiStack'
import {
  Link,
  ToastNotificationVariant,
} from '../../../../src/ui-components/util/Interfaces'
import { LookupType } from '../../../../src/helpers/lookupType'
import QuotesServiceProvider from '../../../../src/services/QuotesServiceProvider'
import VehicleServiceProvider from '../../../../src/services/VehicleServiceProvider'
import {
  LaborDTO,
  Location,
  PartDTO,
  QuoteBody,
  QuoteRequest,
  SpecificationsDTO,
} from './interfaces'
import {
  DateRange,
  getDefaultRanges,
} from '../../../../src/ui-components/forms/DateRanges'
import { MY_PLACE_SELLER } from 'common'
import { getVehicleLabor, getVehicleSpecifications } from 'cart'
import { store } from 'store'
import { GaTrackOption } from 'src/config/analytics/tracking/utils'
import { hawkSearchProvider } from 'src/config/analytics/core/providers/HawkSearchServiceProvider'
import { prepareEventData } from 'src/helpers/HawkSearchEventHelpers'

export class QuoteStore {
  public saveAsQuoteLoading = false

  public quotesHistoryLoading = false

  public totalCount = 0

  public dateRange: DateRange

  public searchTerm = ''

  public quotesHistory: Array<QuotesHistoryItem> = []

  public deleteQuoteLoading = false

  public quoteDetail: QuoteResponse[] | undefined = undefined

  public quoteCart: CartVehicle[] | undefined = undefined

  public quoteDetailLoading = false

  public quoteFormChanged = false

  public updatedQuoteData: OrderFormData | null = null

  public quotesSelectedLabel = undefined

  public showQuoteDetailsMobile = false
  public sellerData: ISeller[] = []

  constructor() {
    makeAutoObservable(this)
  }

  public setShowQuoteDetailsMobile = (quoteDetailsMobile: boolean): void => {
    this.showQuoteDetailsMobile = quoteDetailsMobile
    if (quoteDetailsMobile) {
      this.quoteCart = undefined
      this.quoteDetailLoading = true
    }
  }

  public setQuoteFormChanged = (isChanged: boolean): void => {
    this.quoteFormChanged = isChanged
  }

  public getSellerInfoData = async (
    coverageKey: string | null
  ): Promise<ISeller> => {
    if (!coverageKey) {
      return MY_PLACE_SELLER
    }
    try {
      const response = await MultiSellerServicesProvider.getActiveSellers()
      const match = (response || []).find(
        (item) => item.coverageKey === coverageKey
      )
      return match ? match : MY_PLACE_SELLER
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      return MY_PLACE_SELLER
    }
  }

  public setUpdatedQuoteData = (newQuoteData: OrderFormData): void => {
    this.updatedQuoteData = newQuoteData
  }

  private clearSearchFieldAndFilter = (): void => {
    this.searchTerm = ''
    const today = endOfDay(new Date())
    const ranges = getDefaultRanges(today)?.slice(0, 4)
    const range = ranges.find((r) => r.id === 'all')
    this.setDateFilter(range)
  }

  private setSaveAsQuoteLoading = (set: boolean): void => {
    this.saveAsQuoteLoading = set
  }

  public setDateFilter = (range: DateRange): void => {
    this.dateRange = range
    // if (fetchQuotesHistory) {
    //   this.getQuotesHistory()
    // }
  }

  public updateFilter = (navigation = true): void => {
    if (navigation) {
      history.navigate(
        `/quotes?fromDate=${this.dateRange?.startDate?.toISOString() ?? ''}&dateRangeId=${this.dateRange?.id}&toDate=${
          this.dateRange?.endDate?.toISOString() ?? ''
        }`
      )
    }
  }

  public setSearchTerm = (searchTerm: string): void => {
    this.searchTerm = searchTerm
    this.getQuotesHistory(undefined, undefined)
  }

  private mapLaborsForQuotes = (labors: Array<LaborItem>): Array<LaborDTO> => {
    return labors?.map((labor) => ({
      details: labor.details,
      hours: Number(labor.hours),
      laborId: labor.laborId,
      description: labor.laborDescription,
      rate: labor.rate,
      notes: '',
      skillLevel: labor.skillLevel,
      warrantyHrs: Number(labor.warrantyHrs),
      miscellaneousText: labor.miscellaneousText,
    }))
  }

  private mapSpecificationsForQuotes = (
    specifications: Array<VehicleSpecificationCartItem>
  ): Array<SpecificationsDTO> => {
    return specifications?.map((specification) => ({
      description: specification.description,
      extendedDescription: specification.extendedDescription,
      groupId: specification.groupId,
      miscellaneousTexts: specification.miscellaneousTexts?.join(', '),
      partNumber: specification.partNumber,
      years: specification.years,
    }))
  }

  private mapSpecificationsFromQuoteDetails = (
    specifications: Array<QuoteSpecification>
  ): Array<SpecificationsDTO> => {
    return specifications?.map((specification) => ({
      description: specification.description,
      extendedDescription: specification.extendedDescription,
      groupId: specification.groupId,
      miscellaneousTexts: specification.miscellaneousTexts,
      partNumber: specification.partNumber,
      years: specification.years,
    }))
  }

  private mapPartsForQuotes = (
    products: Array<ShoppingCartProduct>
  ): Array<PartDTO> => {
    return (
      (products || [])
        .map((product) => {
          const locations = []
          product.orderSelections.forEach((orderSelection) => {
            if (orderSelection.quantityRequested > 0) {
              locations.push(
                product.location.find(
                  (loc) => loc.locationId === orderSelection.locationId
                )
              )
            }
          })
          return {
            description: product.description,
            lineCode: product.lineCode,
            orderNumber: product.orderNumber?.toString(),
            partNumber: product.partNumber,
            locations: locations.map((location) => ({
              id: location?.id,
              availability: location?.qtyAvailable,
              coreCost: location?.coreCost,
              coreList: location?.coreList,
              cost: location?.cost,
              list: location?.list,
              qty: product.orderSelections[0]?.quantityRequested, // this is required to save/update requested qty
              snCalled: location?.called,
              snSeqNo: location?.locationId,
            })),
            wsSessionId: '',
          }
        })
        // Filter parts without any locations.
        .filter((product) => product.locations.length > 0)
    )
  }

  private getVehicleById = async (id: number): Promise<Vehicle> => {
    if (id === 0 || id === undefined) return miscellaneousVehicle
    try {
      const vehicleI = await VehicleServiceProvider.getVehicleById(id)
      return {
        year: { id: vehicleI.yearCode, value: vehicleI.year },
        make: { id: vehicleI.makeCode, value: vehicleI.make },
        model: { id: vehicleI.modelCode, value: vehicleI.model },
        subModel: {
          id: vehicleI?.subModelCode,
          value: vehicleI?.subModel || '',
        },
        engine: { id: vehicleI.engineCode, value: vehicleI.engine },
        modelType: { id: vehicleI.modelTypeCode, value: vehicleI.modelType },
        type: { id: vehicleI.type?.id, value: vehicleI.type?.value },
      }
    } catch (_e) {
      throw new Error('quoteVehicleNotFound')
    }
  }

  public getQuoteById = async (id: string): Promise<QuoteResponse> => {
    return QuotesServiceProvider.fetchQuoteById(id)
  }

  public getQuoteByCartId = async (
    id: string | undefined
  ): Promise<QuoteResponse[]> => {
    return QuotesServiceProvider.fetchQuoteByCartId(id)
  }

  private mapLocations = (
    locations: Location[]
  ): Array<ProductLocationModel> => {
    return locations.map(
      (l) =>
        ({
          ...l,
          qtyAvailable: l.availability ?? 0,
          buyQty: l.qty,
          called: l.snCalled,
          locationId: l.snSeqNo?.toString() ?? '',
          coreCost: l.coreCost ?? 0,
          locType: l.snSeqNo === 100 ? LocationType.PRIMARY : l.locType,
          isSelected: false,
          packNumUnits: 1,
        }) as ProductLocationModel
    )
  }

  private mapToCartProduct = (part: Part): ShoppingCartProduct => {
    const location = this.mapLocations(part.locations)

    return {
      partNumber: part.partNumber,
      description: part.description,
      lineCode: part.lineCode,
      location,
      orderSelections: [
        {
          quantityRequested: location?.[0]?.buyQty ?? 1,
          locationId: location?.[0]?.locationId?.toString(),
        },
      ],
    }
  }

  public getCartVehicleFromQuote = (
    quote: QuoteResponse,
    vehicle: Vehicle
  ): CartVehicle => {
    const products = quote.parts.map((part) => this.mapToCartProduct(part))

    const cartVehicle: CartVehicle = {
      vehicle,
      products,
      laborResults: quote.labors,
      specifications: quote.specifications?.map((specification) => ({
        hash:
          specification.partNumber +
          specification.years +
          specification.description,
        ...specification,
        miscellaneousTexts: specification.miscellaneousTexts?.split(','),
      })),
    }

    return cartVehicle
  }

  // private fetchQuoteInfo = async (
  //   quoteId: string
  // ): Promise<{ quote: QuoteResponse; vehicleCart: CartVehicle }> => {
  //   const quote = await this.getQuoteById(quoteId)
  //   const vehicle = await this.getVehicleById(quote.vehicleId)
  //   const vehicleCart = this.getCartVehicleFromQuote(quote, vehicle)

  //   this.clearSearchFieldAndFilter()
  //   return { quote, vehicleCart }
  // }

  public fetchCartQuoteInfo = async (
    cartId: string | undefined
  ): Promise<{ quote: QuoteResponse[]; vehicleCarts: CartVehicle[] }> => {
    const quotes = await this.getQuoteByCartId(cartId)
    if (!quotes?.length) {
      return { quote: [], vehicleCarts: [] }
    }
    const vehicles: Vehicle[] = await Promise.all(
      quotes.map((quoteVehicle) => this.getVehicleById(quoteVehicle.vehicleId))
    )
    const vehicleCarts = quotes.map((quote) =>
      this.getCartVehicleFromQuote(
        quote,
        vehicles.find((vehicle) => vehicle.id === quote.vehicleId) ||
          vehicles[0]
      )
    )

    this.clearSearchFieldAndFilter()
    return { quote: quotes, vehicleCarts }
  }

  public updateStoreWithQuoteDetails = async (id: string): Promise<void> => {
    this.quoteDetailLoading = true
    try {
      const { quote, vehicleCarts } = await this.fetchCartQuoteInfo(id)
      this.quoteDetail = quote
      this.quoteCart = vehicleCarts
    } finally {
      this.quoteDetailLoading = false
    }
  }

  public editQuote = async (
    cartData: CartVehicle,
    quoteId: string | number,
    orderType?: string,
    clearCart = false,
    labors?: QuoteLabor[],
    specifications?: QuoteSpecification[]
  ): Promise<void> => {
    const { orderFormData, products, vehicle } = cartData

    try {
      const mappedLabors = this.mapLaborsForQuotes(
        labors ?? getVehicleLabor(store.getState(), vehicle)
      )

      const mappedSpecifications = specifications
        ? this.mapSpecificationsFromQuoteDetails(specifications)
        : this.mapSpecificationsForQuotes(
            getVehicleSpecifications(store.getState(), vehicle)
          )

      const requestBody: QuoteBody = {
        quoteId: Number(quoteId),
        comment: orderFormData?.personalNote,
        customerName: orderFormData?.customerName || '',
        description: orderFormData?.noteToStore,
        poNo: orderFormData?.poNumber
          ? String(orderFormData.poNumber)
          : undefined,
        type: orderType,
        parts: this.mapPartsForQuotes(products),
        labors: mappedLabors,
        specifications: mappedSpecifications,
      }

      await QuotesServiceProvider.editQuote(requestBody)

      if (clearCart) {
        store.dispatch(
          cartSlice.actions.removeVehicleFromCart({
            vehicle,
            track: GaTrackOption.doNotTrack,
          })
        )
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error editing quote:', error)

      // Optional: surface error to UI
      // StoreInstances.uiStore.displayErrorNotification(
      //   error instanceof Error ? error.message : 'Unknown error'
      // )
    } finally {
      // Optional: handle loading state if needed
      // this.quoteDetailLoading = false
    }
  }

  public getQuotesHistory = async (start = 0, limit = 10): Promise<void> => {
    try {
      this.quotesHistoryLoading = true
      const { orgId } = StoreInstances?.userStore?.preferences || {
        orgId: undefined,
      }

      const filters: QuotesFilters = {
        ...(this.dateRange?.startDate && {
          fromDate: format(this.dateRange?.startDate, 'yyyy-MM-dd'),
        }),
        ...(this.dateRange?.endDate && {
          toDate: format(this.dateRange?.endDate, 'yyyy-MM-dd'),
        }),
        searchTerm: this.searchTerm,
      }

      const response = await QuotesServiceProvider.getQuotesHistory(
        filters,
        { orgId },
        start,
        limit
      )
      this.quotesHistory = response.data
      this.totalCount = response.totalCount
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      StoreInstances.uiStore.displayErrorNotification(e.message)
    } finally {
      this.quotesHistoryLoading = false
    }
  }

  public deleteQuotes = async (quoteId?: number): Promise<void> => {
    try {
      this.deleteQuoteLoading = true
      await QuotesServiceProvider.deleteQuote(quoteId)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      throw new Error(e.toString())
    } finally {
      this.deleteQuoteLoading = false
      this.getQuotesHistory()
    }
  }

  public deleteQuotesByTimePeriod = async (months?: number): Promise<void> => {
    try {
      this.deleteQuoteLoading = true
      await QuotesServiceProvider.deleteQuoteByTime(months)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      throw new Error(e.toString())
    } finally {
      this.deleteQuoteLoading = false
      this.getQuotesHistory()
    }
  }

  private static isAutoLocationChangeEnabled = (): boolean => {
    return (
      StoreInstances.userStore?.preferences?.findit_orderIfNotAvail === 'true'
    )
  }

  public flattenVehicleCartData = (
    data: CartVehicle[]
  ): {
    products: ShoppingCartProduct[]
    laborResults: LaborItem[]
    specifications: VehicleSpecificationCartItem[]
  } => {
    return data.reduce(
      (acc, { products, laborResults, specifications }) => {
        acc.products.push(...(products ?? []))
        acc.laborResults.push(...(laborResults ?? []))
        acc.specifications.push(...(specifications ?? []))
        return acc
      },
      {
        products: [] as ShoppingCartProduct[],
        laborResults: [] as LaborItem[],
        specifications: [] as VehicleSpecificationCartItem[],
      }
    )
  }

  public addToCart = async (
    quoteId: string,
    viewCartLink?: Link
  ): Promise<void> => {
    if (!quoteId) {
      return
    }
    const { vehicleCarts, quote } = await this.fetchCartQuoteInfo(quoteId)
    if (!quote) {
      return
    }
    const sellerQuoteIds = quote.map((q) => q.id)

    const vehicleCart = vehicleCarts?.[0]
    const vehicle = vehicleCart?.vehicle

    const allPoNumbers = [...new Set(quote.map((q) => q.poNo))]
    const poIsSameForAllSellers = allPoNumbers?.length === 1

    const firstQoute = quote?.[0]
    const globalPoNumber = poIsSameForAllSellers ? firstQoute?.poNo : ''
    const customerName = firstQoute?.customerName
    const globalPersonalNote = firstQoute?.comment
    const globalNoteToStore = firstQoute?.description

    await StoreInstances?.searchStore?.updateCurrentVehicle(vehicle)

    if (vehicle.id !== miscellaneousVehicle.id) {
      vehicle.id = StoreInstances?.searchStore?.currentVehicle?.id // The new generated id
    }

    const shoppingCartVehicle = findCartVehicle(store.getState().cart, vehicle)

    if (!shoppingCartVehicle) {
      return
    }

    const vehicleData = this.flattenVehicleCartData(vehicleCarts)
    store.dispatch(
      cartSlice.actions.setVehicleOrderFormData({
        vehicle: toJS(vehicle),
        quoteSourceId: quoteId,
        sellerQuoteIds,
        orderformData: {
          poNumber: globalPoNumber,
          personalNote: globalPersonalNote,
          noteToStore: globalNoteToStore,
          customerName,
        },
      })
    )

    let totalSkipped = 0
    let totalProducts = 0
    vehicleCarts.forEach((val) => {
      totalProducts += val.products?.length ?? 0
    })

    for (let index = 0; index < vehicleCarts.length; index++) {
      const val = vehicleCarts[index]
      const { poNo, description, coverageKey } = quote[index]
      if ((val.laborResults?.length ?? 0) > 0) {
        store.dispatch(
          cartSlice.actions.setVehicleLaborData({
            vehicle: toJS(val.vehicle),
            laborItems: toJS(val.laborResults ?? []),
          })
        )
      }

      if ((val.specifications?.length ?? 0) > 0) {
        store.dispatch(
          cartSlice.actions.setVehicleSpecifications({
            vehicle: toJS(val.vehicle),
            specifications: toJS(val.specifications ?? []),
          })
        )
      }

      const coverageKeyString = coverageKey?.toString() || ''
      const seller = await this.getSellerInfoData(coverageKeyString)

      totalSkipped += await this.addProductsToCart(
        val.products,
        val.vehicle,
        seller
      )

      // Replace sellerStoreInstance.updateSellerFormFields with Redux dispatches
      store.dispatch(
        cartSlice.actions.setFieldOnSeller({
          field: 'poNumber',
          vehicle: val.vehicle,
          seller: seller as SellerWithLocations,
          value: poNo || '',
        })
      )

      store.dispatch(
        cartSlice.actions.setFieldOnSeller({
          field: 'noteToStore',
          vehicle: val.vehicle,
          seller: seller as SellerWithLocations,
          value: description || '',
        })
      )
    }

    if (totalProducts > 0 && totalSkipped === totalProducts) {
      ShowNotification({
        message: 'noPartsCanBeAddedToCart',
        variant: ToastNotificationVariant.ERROR,
      })
      return
    }

    const vehicles = vehicleCarts.map((vC) => vC.vehicle)

    const cartVehicles = vehicles?.map((v) =>
      findCartVehicle(store.getState().cart, v)
    )

    const inCartProducts = cartVehicles
      ?.flatMap((cV) =>
        cV?.sellers.flatMap((s) => s.locations?.flatMap((l) => l.parts))
      )
      .filter((v) => v !== undefined)

    if (inCartProducts) {
      showCartPartComparisionNotification(
        vehicleData?.products,
        inCartProducts,
        vehicleData?.laborResults || [],
        vehicleData?.specifications || [],
        viewCartLink || {
          text: 'Default Text',
          action: () => {
            //
          },
          // Add other required properties of `Link` here
        }
      )
    }

    vehicleData?.products?.forEach((product) => {
      const orderSelection = product.orderSelections?.[0]
      const location = product.location?.find(
        (loc) =>
          loc.locationId?.toString() === orderSelection?.locationId?.toString()
      )
      hawkSearchProvider.trackEvent(
        'Add2Cart',
        prepareEventData('Add2Cart', {
          productId: `${product.lineCode} ${product.partNumber}`,
          price: location?.cost,
          quantity: orderSelection?.quantityRequested,
          vehicle: vehicleCart?.vehicle,
        })
      )
    })
  }

  private addProductsToCart = async (
    products: ShoppingCartProduct[],
    vehicle: Vehicle,
    seller: ISeller
  ): Promise<number> => {
    const autoLocationChangeEnabled = QuoteStore.isAutoLocationChangeEnabled()

    const productDetails = products.map((product) => {
      const { orderSelections } = product
      return {
        product: { ...product, type: LineType.part, orderSelections: [] },
        locationId: orderSelections?.[0]?.locationId || '100',
        quantity: orderSelections?.[0]?.quantityRequested || 1,
        vehicle,
        autoLocationChange: autoLocationChangeEnabled,
        seller,
      }
    })

    const updatedParts = await getUpdatedPartsList(productDetails)

    const addableDetails = productDetails.filter((detail, i) => {
      const loc = updatedParts[i]?.location?.find(
        (l) => l.locationId?.toString() === detail.locationId?.toString()
      )
      return loc?.locType !== LocationType.VIEW_ONLY
    })

    const skippedCount = productDetails.length - addableDetails.length

    if (addableDetails.length > 0) {
      const addableWithUpdatedProducts = addableDetails.map((detail) => ({
        ...detail,
        product: updatedParts[productDetails.indexOf(detail)],
      }))
      await setQtyAtLocationBulk(addableWithUpdatedProducts, false)
    }

    return skippedCount
  }

  public saveMultiSellerQuote = async (
    sellerCartVehicle: SellerCartVehicle
  ): Promise<void> => {
    try {
      this.setSaveAsQuoteLoading(true)
      const requestBody: QuoteRequest[] =
        this.createMultiSellerQuoteRequest(sellerCartVehicle)

      await QuotesServiceProvider.saveAsQuote(requestBody)

      store.dispatch(
        cartSlice.actions.removeVehicleFromCart({
          vehicle: sellerCartVehicle.vehicle,
          track: GaTrackOption.doNotTrack,
        })
      )

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      StoreInstances.uiStore.displayErrorNotification(JSON.stringify(e.message))
    } finally {
      this.setSaveAsQuoteLoading(false)
    }
  }

  public updateMultiSellerQuote = async (
    sellerCartVehicle: SellerCartVehicle
  ): Promise<void> => {
    try {
      this.setSaveAsQuoteLoading(true)
      const quoteRequests =
        this.createMultiSellerQuoteRequest(sellerCartVehicle)

      const updatePromises = quoteRequests.map((request, index) => {
        const quoteId =
          sellerCartVehicle.sellerQuoteIds?.[index] ||
          sellerCartVehicle.quoteSourceId
        if (quoteId) {
          const body: QuoteBody = { ...request.quote, quoteId: Number(quoteId) }
          return QuotesServiceProvider.editQuote(body)
        }
        return Promise.resolve()
      })

      await Promise.all(updatePromises)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (e: any) {
      StoreInstances.uiStore.displayErrorNotification(JSON.stringify(e.message))
    } finally {
      this.setSaveAsQuoteLoading(false)
    }
  }

  createMultiSellerQuoteRequest = (
    sellerCartVehicle: SellerCartVehicle
  ): QuoteRequest[] => {
    const country = StoreInstances.userStore?.country
    if (!country) {
      throw new Error('Country is not defined in userStore')
    }

    const hasNoSellers = !sellerCartVehicle.sellers?.length

    if (hasNoSellers) {
      return [
        {
          lookupType: LookupType.CAR,
          countryId: country.countryId,
          quote: {
            comment: sellerCartVehicle.orderFormData?.personalNote || '',
            customerName: sellerCartVehicle?.orderFormData?.customerName || '',
            description: sellerCartVehicle.orderFormData?.noteToStore || '',
            poNo: sellerCartVehicle.orderFormData?.poNumber || '',
            parts: [],
            labors: this.mapLaborsForQuotes(
              getVehicleLabor(store.getState(), sellerCartVehicle.vehicle)
            ),
            specifications: this.mapSpecificationsForQuotes(
              getVehicleSpecifications(
                store.getState(),
                sellerCartVehicle.vehicle
              )
            ),
            type: 'R',
            multiseller: false,
            coverageKey: '',
            vehicle: sellerCartVehicle.vehicle,
          },
        },
      ]
    }

    return sellerCartVehicle.sellers.map((seller) => {
      return {
        lookupType: LookupType.CAR,
        countryId: country.countryId,
        quote: {
          comment: sellerCartVehicle.orderFormData?.personalNote,
          customerName: sellerCartVehicle?.orderFormData?.customerName || '',
          description:
            seller.noteToStore ||
            sellerCartVehicle.orderFormData?.noteToStore ||
            '',
          poNo:
            seller.poNumber || sellerCartVehicle.orderFormData?.poNumber || '',
          parts: seller.locations.flatMap((location) =>
            location.parts.map((product) => ({
              description: product.description,
              lineCode: product.lineCode,
              orderNumber: product.orderNumber?.toString(),
              partNumber: product.partNumber,
              locations: product.location
                .filter(
                  (loc) =>
                    loc?.locationId?.toString() ===
                    location?.locationId?.toString()
                )
                .map((loc) => ({
                  availability: loc?.qtyAvailable,
                  coreCost: loc?.coreCost,
                  coreList: loc?.coreList,
                  cost: loc?.cost,
                  list: loc?.list,
                  qty: product?.quantityRequested,
                  snCalled: loc?.called,
                  snSeqNo: Number(loc?.locationId),
                })),
              wsSessionId: '',
            }))
          ),
          labors: seller.coverageKey
            ? []
            : this.mapLaborsForQuotes(
                getVehicleLabor(store.getState(), sellerCartVehicle.vehicle)
              ),
          specifications: seller.coverageKey
            ? []
            : this.mapSpecificationsForQuotes(
                getVehicleSpecifications(
                  store.getState(),
                  sellerCartVehicle.vehicle
                )
              ),
          type: 'R',
          multiseller: seller.coverageKey !== '',
          coverageKey: seller.coverageKey || '',
          vehicle: sellerCartVehicle.vehicle,
        },
      }
    })
  }
}

export const quoteStoreInstance = new QuoteStore()

export const QuoteContext = createContext<QuoteStore>(quoteStoreInstance)

export const useQuoteStore = (): QuoteStore => {
  const quotesStore = useContext(QuoteContext)
  if (!quotesStore) {
    throw new Error(
      'No QuoteContext.Provider found when calling useQuoteStore.'
    )
  }
  return quotesStore
}
